<template>
  <div class="game-table-flush">
  <vue-good-table 
    styleClass="vgt-table condensed vgt-flush"
    v-bind="tableOptions"
    :columns="columns"
    :rows="rows"
    :pagination-options="paginationFor(rows)"
    compactMode>
    <template #table-row="props">
      <div v-if="props.column.field === 'ISINName'" class="d-flex align-items-center gap-2 py-0">
        <div
          class="d-flex align-items-center justify-content-center flex-shrink-0 portfolio-logo"
          :class="logoClass(props.row)"
        >
          <template v-if="isCash(props.row)">
            <span>CZK</span>
          </template>
          <template v-else>
            <img
              v-if="showShareLogo(props.row)"
              :src="shareLogoUrl(props.row.ISIN)"
              :alt="props.row.ISINName"
              @error="onLogoError(props.row.ISIN)"
            />
            <span v-else>{{ shareMonogram(props.row.ISINName) }}</span>
          </template>
        </div>
        <span class="text-sm font-weight-bold text-truncate">{{ props.row.ISINName }}</span>
        <button
          v-if="props.row.ISINID"
          type="button"
          class="forum-chart-btn"
          :class="{ 'forum-chart-btn-dark': isDarkMode }"
          :title="'Graf: ' + props.row.ISINName"
          @click.stop="openChart(props.row)"
        >
          <fa-icon icon="fa-solid fa-chart-line" />
        </button>
        <button
          v-if="props.row.ISINID"
          type="button"
          class="forum-bubble-btn"
          :class="{ 'forum-bubble-btn-dark': isDarkMode }"
          :title="'Fórum: ' + props.row.ISINName"
          @click.stop="openShareForum(props.row)"
        >
          <fa-icon icon="fa-regular fa-comment-dots" />
        </button>
      </div>
      <span v-else-if="props.column.field == 'Pos'">
        {{ isCash(props.row) ? '' : props.formattedRow.Pos }}
      </span>
      <div v-else-if="props.column.field == 'Opc'" class="d-flex flex-column align-items-end">
        <template v-if="!isCash(props.row)">
          <span>{{ formatFn(props.row.Opc) }}</span>
          <span v-if="openingValue(props.row) != null" class="text-xs stacked-sub">
            {{ formatFn(openingValue(props.row)) }}
          </span>
        </template>
      </div>
      <div v-else-if="props.column.field == 'Apc'" class="d-flex flex-column align-items-end">
        <template v-if="isCash(props.row)">
          <span
            :class="{
              'text-success' : props.row.PChT == 1,
              'text-info' : props.row.PChT == 0,
              'text-danger' : props.row.PChT == -1}"
          >
            {{ formatFn(props.row.Amt) }}
            <fa-icon v-if="props.row.PChT == 1" icon="fa-solid fa-arrow-up" size="xs" />
            <fa-icon v-if="props.row.PChT == -1" icon="fa-solid fa-arrow-down" size="xs" />
          </span>
        </template>
        <template v-else>
          <span :class="{
            'text-success' : props.row.AChT == 1,
            'text-info' : props.row.AChT == 0,
            'text-danger' : props.row.AChT == -1}">
            {{ formatFn(props.row.Apc) }}
            <fa-icon v-if="props.row.AChT == 1" icon="fa-solid fa-arrow-up" size="xs" />
            <fa-icon v-if="props.row.AChT == -1" icon="fa-solid fa-arrow-down" size="xs" />
          </span>
          <span
            v-if="props.row.Amt != null && props.row.Amt !== ''"
            class="text-xs stacked-sub"
            :class="{
              'text-success' : props.row.PChT == 1,
              'text-info' : props.row.PChT == 0,
              'text-danger' : props.row.PChT == -1}"
          >
            {{ formatFn(props.row.Amt) }}
            <fa-icon v-if="props.row.PChT == 1" icon="fa-solid fa-arrow-up" size="xs" />
            <fa-icon v-if="props.row.PChT == -1" icon="fa-solid fa-arrow-down" size="xs" />
          </span>
        </template>
      </div>
      <span v-else>
        {{props.formattedRow[props.column.field]}}
      </span>
    </template>
    <template #emptystat>
      <div class="text-center">
        <span>Žádné běžící hry</span>
      </div>
    </template>
  </vue-good-table>
  </div>
</template>

<script setup>
import { reactive, computed } from 'vue';
import { useStore } from 'vuex';
import { tableOptions, paginationFor } from '../../../../helpers/tableOptions';
import { shareLogoUrl, shareMonogram } from '@/helpers/shareLogos';

const props = defineProps({
  rows: {
    type: [Array, Object]
  }
});

const store = useStore();
const isDarkMode = computed(() => !!store.state.isDarkMode);
const logoFailed = reactive({});

const formatFn = (number) => {
  return store.getters.formatTableNumber(number);
}

const isCash = (row) => {
  const name = String(row?.ISINName || '').toLowerCase();
  if (name.includes('nevyužitý') || name.includes('nevyuzity') || name.includes('peníze')) return true;
  const id = row?.ISINID;
  const isin = row?.ISIN;
  const hasId = id != null && String(id).trim() !== '' && Number(id) !== 0;
  const hasIsin = isin != null && String(isin).trim() !== '' && String(isin) !== '0' && isin !== 'CZK';
  return !hasId && !hasIsin;
};

const onLogoError = (isin) => {
  if (isin) logoFailed[isin] = true;
};

const showShareLogo = (row) => !!(shareLogoUrl(row.ISIN) && !logoFailed[row.ISIN]);

const logoClass = (row) => {
  if (isCash(row)) return 'portfolio-logo-cash';
  if (!showShareLogo(row)) return 'portfolio-logo-fallback';
  return '';
};

const openChart = (row) => {
  if (!row?.ISINID) return;
  store.dispatch('fetchISINChartData', row);
};

const openShareForum = (row) => {
  if (!row?.ISINID) return;
  store.dispatch('openForum', {
    title: row.ISINName || 'Akcie',
    ISINID: row.ISINID,
    ISIN: row.ISIN
  });
};

const openingValue = (row) => {
  const pos = Number(row?.Pos);
  const opc = Number(row?.Opc);
  if (!Number.isFinite(pos) || !Number.isFinite(opc)) return null;
  return pos * opc;
};

const columns = reactive([
  { 
    label: 'Aktivum',
    field: 'ISINName',
    tooltip: 'Název/identifikace aktiva',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder',
    tdClass: 'text-sm font-weight-bold align-middle td-middle',
  },
  { 
    label: 'Pozice',
    field: 'Pos',
    tooltip: 'Množství',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder text-center',
    tdClass: 'text-sm font-weight-bold text-center align-middle td-middle',
  },
  {
    label: 'Ot.cena',
    field: 'Opc',
    tooltip: 'Otevírací cena a otevírací hodnota pozice',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder text-end',
    tdClass: 'text-sm font-weight-bold text-end',
    formatFn: formatFn
  },
  {
    label: 'Akt.cena',
    field: 'Apc',
    tooltip: 'Aktuální cena a aktuální hodnota pozice',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder text-end',
    tdClass: 'text-sm font-weight-bold text-end',
    formatFn: formatFn
  }
]);

</script>

<style scoped>
.stacked-sub {
  color: #94A3B8;
  font-weight: 600;
}

:deep(td.td-middle) {
  vertical-align: middle !important;
}

.portfolio-logo {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  overflow: hidden;
}

.portfolio-logo img {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.portfolio-logo-fallback {
  background: rgba(100, 116, 139, 0.16);
  color: #64748b;
  font-size: 0.55rem;
  font-weight: 700;
}

.portfolio-logo-cash {
  width: 20px;
  height: 20px;
  overflow: visible;
  background: rgba(5, 150, 105, 0.16);
  color: #059669;
  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: -0.06em;
  line-height: 1;
}

.forum-chart-btn {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1.5px solid #7DD3FC;
  background: transparent;
  color: #0EA5E9;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 0.65rem;
}

.forum-chart-btn:hover {
  color: #0284C7;
  border-color: #0EA5E9;
  background: rgba(14, 165, 233, 0.12);
}

.forum-chart-btn-dark {
  border-color: #38BDF8;
  color: #38BDF8;
}

.forum-chart-btn-dark:hover {
  color: #7DD3FC;
  border-color: #7DD3FC;
  background: rgba(56, 189, 248, 0.16);
}

.forum-bubble-btn {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1.5px solid #CBD5E1;
  background: transparent;
  color: #64748B;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 0.7rem;
}

.forum-bubble-btn:hover {
  color: #FF8A4C;
  border-color: #FF8A4C;
  background: rgba(255, 138, 76, 0.08);
}

.forum-bubble-btn-dark {
  border-color: #475569;
  color: #94A3B8;
}

.forum-bubble-btn-dark:hover {
  color: #FF8A4C;
  border-color: #FF8A4C;
  background: rgba(255, 138, 76, 0.14);
}
</style>
